import React, { useCallback, useRef, useState } from 'react';
import { Box } from 'grommet';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FileBox, MCButton, View } from '../basic';
import { checkAvatarDimension, getFileType, getThumbnailFromVideoFile, remCss } from 'src/helpers';
import { DropBox } from './DropBox';
import { popupModel } from './index';
import { BASE_ICONS } from '../svg';
import LANG_CONST from '../../helpers/lang/const';
import AlertManager from 'components/alert/AlertManager';

const SortableItem = ({ value, id, width, height, onClose }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width,
    height,
    zIndex: isDragging ? '100' : 'auto',
    opacity: isDragging ? 0.3 : 1
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Box>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <MCButton
            style={{ position: 'absolute', top: 7, right: 7 }}
            iconname={BASE_ICONS.CLOSE}
            onClick={onClose}
            primary
            dark
            circle
            width={20}
            padding={15}
          />
        </div>
        <div
          {...listeners}
          {...attributes}
          style={{
            border: 'none',
            backgroundColor: 'transparent',
            outline: 'none',
            padding: 0
          }}
        >
          {value}
        </div>
      </Box>
    </div>
  );
};

export const MediaOrderDropBox = ({
  value = [],
  onChange,
  template = 1,
  fileBoxProps,
  dropImage,
  labelIcon = LANG_CONST.buttons.drap_or_click_here,
  styles = { width: remCss(100), height: remCss(100) },
  ...otherProps
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const scrollRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragStart = (event) => {
    setActiveIndex(value.findIndex((i) => i.counter === event.active.id));
  };

  const handleDragEnd = (event) => {
    setActiveIndex(null);
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = value.findIndex((v) => v.counter === active.id);
      const newIndex = value.findIndex((v) => v.counter === over.id);

      onChange(arrayMove(value, oldIndex, newIndex));
    }
  };

  const scrollToBottom = useCallback(() => {
    setTimeout(
      () =>
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth'
        }),
      100
    );
  }, []);

  const onUpload = useCallback(
    async (n, fileList) => {
      const maxCounter = Math.max(0, ...value.map((f) => f.counter)) + 1;
      let hasImage = false;
      for (let i = 0; i < fileList.length; i++) {
        if (!!dropImage && getFileType(fileList[i]) === 'image') {
          hasImage = true;
          if (!(await checkAvatarDimension(fileList[i]))) return;
        }
      }
      let error = null;
      const files = await Promise.all(
        fileList.map(async (f, index) => {
          let thumbnail;
          let type = getFileType(f);
          if (type === 'video') {
            thumbnail = await getThumbnailFromVideoFile(f);
            if (thumbnail === null) {
              error = LANG_CONST.validation.video_invalid;
            }
          }
          const counter = maxCounter + index;
          const file = new File([f], `file-${counter}.` + f.name.split('.').pop(), {
            type: f.type
          });
          return {
            file,
            thumbnail,
            counter,
            originalName: file.name
          };
        })
      );

      if (error) {
        return AlertManager.popup(AlertManager.TYPES.WARN, {
          close: false,
          title: error,
          actionButton: {
            label: LANG_CONST.buttons.ok,
            action: (actions) => actions.hide()
          }
        });
      }

      if (hasImage && !!dropImage) {
        return popupModel.openCropImage({
          ...dropImage,
          src: files.map((f) => f.file),
          onSave: (v) => {
            onChange([...value, ...files.map((f, index) => ({ ...f, file: v[index] }))]);
            scrollToBottom();
          }
        });
      }

      onChange([...value, ...files]);

      if (files.length > 0) {
        scrollToBottom();
      }
    },
    [dropImage, onChange, scrollToBottom, value]
  );

  const children = value.map((item) => (
    <FileBox
      template={1}
      videoProps={{ controls: false, playsinline: false, playing: false }}
      onlyPreview={true}
      key={item.counter}
      file={item}
      onFile={() => {}}
      onClose={null}
      styles={styles}
      showFileName={false}
      {...fileBoxProps}
    />
  ));

  if (template === 1)
    return (
      <>
        <View style={{ height: remCss(150) }}>
          <DropBox {...otherProps} onUpload={onUpload} multiple={true} labelIcon={labelIcon} />
        </View>
        <View style={{ overflow: 'auto', paddingLeft: remCss(10) }} ref={scrollRef}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
          >
            <Box
              flex={true}
              wrap={true}
              direction='row'
              style={{ maxWidth: '100%', gap: remCss(15) }}
            >
              <SortableContext
                items={value.map(({ counter }) => counter)}
                strategy={rectSortingStrategy}
              >
                {value.map((item, index) => (
                  <SortableItem
                    key={item.counter}
                    id={item.counter}
                    handle={true}
                    value={children[index]}
                    width={styles.width}
                    height={styles.height}
                    onClose={() => onChange(value.filter((v) => v.counter !== item.counter))}
                  />
                ))}
                <DragOverlay>{activeIndex !== null ? children[activeIndex] : null}</DragOverlay>
              </SortableContext>
            </Box>
          </DndContext>
        </View>
      </>
    );

  return (
    <View style={{ overflow: 'auto' }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
      >
        <Box flex={true} wrap={true} direction='row' style={{ maxWidth: '100%', gap: remCss(15) }}>
          <SortableContext
            items={value.map(({ counter }) => counter)}
            strategy={rectSortingStrategy}
          >
            {value.map((item, index) => (
              <SortableItem
                key={item.counter}
                id={item.counter}
                handle={true}
                value={children[index]}
                width={styles.width}
                height={styles.height}
                onClose={() => onChange(value.filter((v) => v.counter !== item.counter))}
              />
            ))}
            <DragOverlay>{activeIndex !== null ? children[activeIndex] : null}</DragOverlay>
          </SortableContext>
          <Box>
            <View
              style={{
                width: styles.width,
                height: styles.height
              }}
            >
              <DropBox
                dropBoxStyles={{
                  padding: 0,
                  margin: 0
                }}
                {...otherProps}
                onUpload={onUpload}
                multiple={true}
                labelIcon={labelIcon}
              />
            </View>
          </Box>
        </Box>
      </DndContext>
    </View>
  );
};
